import { useTranslation } from '@mukil/i18n';
import {
  FieldRow,
  NumericFieldRow,
} from '../../../components/common/field_row';
import { format_money } from '../../../lib/format';
import type { Employee } from '../../../data/types';
import { deriveJurisdiction } from '../employee_form_validation';

/**
 * Read-only Sri Lanka tax facts on the profile's Payroll tab (#26653,
 * ADR-168): residency, which IRD APIT table the employee is withheld under,
 * and the declared primary income a secondary employment is rated on. The
 * single owner of the "Table 04 / 07" wording; the declared income is a monthly figure the employee declares in the entity's base currency, never the pay currency; editing is the existing Edit
 * dialog. Renders nothing outside Sri Lanka (jurisdiction proxy: base
 * currency, as the form dialog uses).
 */
export function ApitBasisRows({
  employee,
  baseCurrency,
}: {
  employee: Employee;
  baseCurrency: string;
}): React.JSX.Element | null {
  const { t, locale } = useTranslation('wp');
  if (deriveJurisdiction(baseCurrency) !== 'LK') return null;

  const nonResident = employee.residency_status === 'non_resident';
  const nonCitizen = employee.is_citizen === false;
  const secondary = employee.apit_employment_type === 'secondary';
  const declared = employee.apit_declared_primary_income_cents ?? null;
  // ADR-170: the API refuses the flag on Table 04 / resident-secondary, so a
  // set flag here is Table 6.1 (primary) or 6.3 (non-resident secondary).
  const borne = employee.apit_borne_by_employer === true;

  return (
    <>
      <FieldRow label={t('employees.form_residency_status')}>
        {nonResident
          ? t('employees.form_residency_non_resident')
          : t('employees.form_residency_resident')}
        {nonResident && nonCitizen
          ? ` ${t('employees.residency_non_citizen_suffix')}`
          : ''}
      </FieldRow>
      <FieldRow label={t('employees.apit_basis')}>
        {borne
          ? secondary
            ? t('employees.apit_basis_employer_borne_secondary')
            : t('employees.apit_basis_employer_borne')
          : secondary
            ? t('employees.apit_basis_secondary')
            : nonResident && nonCitizen
              ? t('employees.apit_basis_primary_non_resident')
              : t('employees.apit_basis_primary')}
      </FieldRow>
      {secondary && (
        <NumericFieldRow label={t('employees.apit_declared_primary_income')}>
          {declared === null
            ? t('employees.apit_not_declared')
            : format_money(declared, baseCurrency, locale)}
        </NumericFieldRow>
      )}
    </>
  );
}
